import React, { useState, useEffect, useContext, useRef, memo } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { 
  FaPlus, FaEdit, FaEye, FaSearch, FaTimes, FaFilter, FaFileExport,
  FaBox, FaClipboardCheck, FaWarehouse, FaExclamationTriangle,
  FaCheckCircle, FaClock, FaShoppingCart, FaBan, FaChevronDown, FaSpinner,
  FaSave, FaCheck, FaCalendarAlt, FaTrash
} from 'react-icons/fa';

// Format number with commas
const formatNumberWithCommas = (number) => {
  const value = typeof number === 'string' ? parseFloat(number) : number;
  if (!value || isNaN(value)) return '0';
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(value);
};

// Enhanced Tiles Search Bar Component
const TilesSearchBar = memo(({ 
  tiles = [], 
  onTileSelect, 
  placeholder = "Search tiles by name, brand, category...",
  className = "",
  darkMode = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTiles, setFilteredTiles] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTiles([]);
      setIsOpen(false);
      return;
    }

    const filtered = tiles.filter(tile => {
      const searchLower = searchTerm.toLowerCase();
      return (
        tile.name?.toLowerCase().includes(searchLower) ||
        tile.brand?.name?.toLowerCase().includes(searchLower) ||
        tile.category?.name?.toLowerCase().includes(searchLower) ||
        tile.type?.name?.toLowerCase().includes(searchLower) ||
        tile.finish?.name?.toLowerCase().includes(searchLower) ||
        `${tile.length}x${tile.width}`.includes(searchLower) ||
        tile.remarks?.toLowerCase().includes(searchLower)
      );
    }).slice(0, 20); // Limit to 20 results

    setFilteredTiles(filtered);
    setIsOpen(filtered.length > 0);
    setSelectedIndex(-1);
  }, [searchTerm, tiles]);

  const handleTileSelect = (tile) => {
    onTileSelect(tile);
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredTiles.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredTiles[selectedIndex]) {
          handleTileSelect(filteredTiles[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        searchRef.current?.blur();
        break;
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        <input
          ref={searchRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (filteredTiles.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-200'}`}
        />
      </div>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className={`absolute z-50 w-full mt-1 border-2 rounded-xl shadow-xl max-h-80 overflow-y-auto ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-100'}`}
        >
          {filteredTiles.map((tile, index) => (
            <div
              key={tile._id}
              onClick={() => handleTileSelect(tile)}
              className={`p-4 cursor-pointer transition-colors duration-150 ${
                index === selectedIndex 
                  ? darkMode ? 'bg-blue-900/30 border-l-4 border-blue-500' : 'bg-blue-50 border-l-4 border-blue-500' 
                  : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              } ${index !== filteredTiles.length - 1 ? (darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100') : ''}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className={`font-semibold text-sm mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {tile.name}
                  </div>
                  <div className={`text-xs space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className="flex flex-wrap gap-2">
                      {tile.brand?.name && (
                        <span className={`px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                          {tile.brand.name}
                        </span>
                      )}
                      {tile.category?.name && (
                        <span className={`px-2 py-0.5 rounded-full ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                          {tile.category.name}
                        </span>
                      )}
                      {tile.type?.name && (
                        <span className={`px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                          {tile.type.name}
                        </span>
                      )}
                    </div>
                    <div className={darkMode ? 'text-gray-500' : 'text-gray-500'}>
                      Size: {tile.length}×{tile.width} {tile.unit} • 
                      Box: {tile.piecesPerBox} pcs
                    </div>
                  </div>
                </div>
                <div className="ml-3 text-right">
                  <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ₹{formatNumberWithCommas(tile.pricePerBox)}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>per box</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// Initialize form state function
const initializeFormState = (userId = '') => ({
  dispatchId: null,
  location: null,
  items: [],
  receiptDate: new Date().toISOString().split('T')[0],
  remarks: '',
  createdBy: userId
});

const TilesReceivedEnhanced = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage changes
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Initialize refs
  const formRef = useRef(null);
  
  // Data source states
  const [dispatches, setDispatches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [receipts, setReceipts] = useState([]);
  
  // Dispatch selection states
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [filteredDispatches, setFilteredDispatches] = useState([]);
  const [dispatchSearchTerm, setDispatchSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Basic states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [receiptId, setReceiptId] = useState(null);

  // Form state
  const [form, setForm] = useState(() => initializeFormState(user?.user?._id));

  // Receipts list states
  const [receiptsSearchTerm, setReceiptsSearchTerm] = useState('');
  const [filteredReceipts, setFilteredReceipts] = useState([]);

  // Dispatch selection modal states
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchSearch, setDispatchSearch] = useState('');
  const [filteredDispatchesModal, setFilteredDispatchesModal] = useState([]);

  // Location selection modal states  
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [filteredLocationsModal, setFilteredLocationsModal] = useState([]);

  // View receipt modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Delivery order commitment tracking
  const [commitments, setCommitments] = useState({});
  const [commitmentsLoading, setCommitmentsLoading] = useState(false);

  // Pagination for receipts
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });

  // Filters
  const [filters, setFilters] = useState({
    dispatchId: '',
    supplier: '',
    location: '',
    startDate: '',
    endDate: ''
  });

  // Load initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load receipts when filters change
  useEffect(() => {
    fetchReceipts();
  }, [pagination.currentPage, filters]);

  // Filter receipts based on search term
  useEffect(() => {
    if (!receiptsSearchTerm.trim()) {
      setFilteredReceipts(receipts);
      return;
    }

    const filtered = receipts.filter(receipt => 
      receipt.receiptId?.toLowerCase().includes(receiptsSearchTerm.toLowerCase()) ||
      receipt.dispatchId?.dispatchId?.toLowerCase().includes(receiptsSearchTerm.toLowerCase()) ||
      receipt.supplier?.name?.toLowerCase().includes(receiptsSearchTerm.toLowerCase()) ||
      receipt.location?.name?.toLowerCase().includes(receiptsSearchTerm.toLowerCase())
    );
    setFilteredReceipts(filtered);
  }, [receiptsSearchTerm, receipts]);

  // Filter dispatches for selection
  useEffect(() => {
    if (!dispatchSearchTerm.trim()) {
      setFilteredDispatches(dispatches.filter(dispatch => 
        dispatch.status === 'in-transit' || dispatch.status === 'pending' || dispatch.status === 'partial'
      ));
      return;
    }

    const filtered = dispatches.filter(dispatch => 
      (dispatch.status === 'in-transit' || dispatch.status === 'pending' || dispatch.status === 'partial') &&
      (dispatch.dispatchId?.toLowerCase().includes(dispatchSearchTerm.toLowerCase()) ||
       dispatch.orderId?.orderId?.toLowerCase().includes(dispatchSearchTerm.toLowerCase()) ||
       dispatch.supplier?.name?.toLowerCase().includes(dispatchSearchTerm.toLowerCase()))
    );
    setFilteredDispatches(filtered);
  }, [dispatchSearchTerm, dispatches]);

  // Filter dispatches for modal
  useEffect(() => {
    if (dispatchSearch.trim()) {
      const filtered = dispatches.filter(dispatch =>
        (dispatch.status === 'in-transit' || dispatch.status === 'pending' || dispatch.status === 'partial') &&
        (dispatch.dispatchId?.toLowerCase().includes(dispatchSearch.toLowerCase()) ||
         dispatch.orderId?.orderId?.toLowerCase().includes(dispatchSearch.toLowerCase()) ||
         dispatch.supplier?.name?.toLowerCase().includes(dispatchSearch.toLowerCase()))
      );
      setFilteredDispatchesModal(filtered);
    } else {
      setFilteredDispatchesModal(dispatches.filter(dispatch => 
        dispatch.status === 'in-transit' || dispatch.status === 'pending' || dispatch.status === 'partial'
      ));
    }
  }, [dispatchSearch, dispatches]);

  // Filter locations for modal
  useEffect(() => {
    if (locationSearch.trim()) {
      const filtered = locations.filter(location =>
        location.name?.toLowerCase().includes(locationSearch.toLowerCase()) ||
        location.address?.toLowerCase().includes(locationSearch.toLowerCase())
      );
      setFilteredLocationsModal(filtered);
    } else {
      setFilteredLocationsModal(locations);
    }
  }, [locationSearch, locations]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [dispatchesRes, locationsRes] = await Promise.all([
        api.get('/tiles-dispatch/available/for-receiving', { 
          params: { company: user?.user?.company?._id } 
        }).catch(() => ({ data: { data: [] } })),
        api.get(`/locations/companyId/${user?.user?.company?._id}`).catch(() => ({ data: [] }))
      ]);

      console.log('🔄 Dispatches API Response:', {
        status: dispatchesRes?.status,
        dataType: typeof dispatchesRes?.data,
        hasData: !!dispatchesRes?.data,
        hasDataProperty: !!dispatchesRes?.data?.data,
        dispatchesLength: dispatchesRes?.data?.data?.length || 0,
        sample: dispatchesRes?.data?.data?.[0]
      });

      console.log('🔄 Locations API Response:', {
        status: locationsRes?.status,
        dataType: typeof locationsRes?.data,
        isArray: Array.isArray(locationsRes?.data),
        locationsLength: locationsRes?.data?.length || 0,
        sample: locationsRes?.data?.[0]
      });

      setDispatches(Array.isArray(dispatchesRes?.data?.data) ? dispatchesRes.data.data : []);
      setLocations(Array.isArray(locationsRes?.data) ? locationsRes.data : []);

      console.log('✅ Initial data loaded:', {
        dispatches: dispatchesRes?.data?.data?.length || 0,
        locations: locationsRes?.data?.length || 0,
        dispatchSamples: dispatchesRes?.data?.data?.slice(0, 2).map(d => ({ id: d._id, dispatchId: d.dispatchId }))
      });
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      Swal.fire('Error', 'Failed to load initial data. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      let params = {
        page: pagination.currentPage,
        limit: 10
      };
      
      if (user?.user?.company?._id) params.company = user.user.company._id;
      if (filters.dispatchId) params.dispatchId = filters.dispatchId;
      if (filters.supplier) params.supplier = filters.supplier;
      if (filters.location) params.location = filters.location;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await api.get('/tiles-received', { params });

      if (response.data) {
        setReceipts(response.data.data.receipts || []);
        setPagination(response.data.data.pagination || pagination);
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
      toast.error('Error fetching receipts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch delivery order commitments when dispatch items and location are set
  const fetchCommitments = async (tileIds, locationId) => {
    if (!tileIds?.length || !locationId) {
      setCommitments({});
      return;
    }
    try {
      setCommitmentsLoading(true);
      const response = await api.post('/tiles-received/check-commitments', {
        tileIds,
        location: locationId
      });
      if (response.data?.success) {
        setCommitments(response.data.data || {});
      }
    } catch (error) {
      console.error('Error checking commitments:', error);
    } finally {
      setCommitmentsLoading(false);
    }
  };

  // Re-check commitments when location changes and items exist
  useEffect(() => {
    if (form.items.length > 0 && form.location?.value) {
      const tileIds = form.items.map(item => item.tileId);
      fetchCommitments(tileIds, form.location.value);
    } else {
      setCommitments({});
    }
  }, [form.location?.value, form.items.length]);

  const handleDispatchSelect = (selected) => {
    if (!selected) {
      setSelectedDispatch(null);
      setForm(prev => ({ ...prev, dispatchId: null, items: [] }));
      return;
    }

    const dispatch = dispatches.find(d => d._id === selected.value);
    if (!dispatch) return;

    setSelectedDispatch(selected);
    
    // Initialize items with dispatched quantities and empty received quantities
    const items = dispatch.items.map(item => {
      const dispatchedBoxes = item.dispatchedBoxes || 0;
      const alreadyReceivedBoxes = item.receivedQuantityBoxes || 0;
      const pendingBoxes = dispatchedBoxes - alreadyReceivedBoxes;
      
      return {
        tileId: item.tileId._id,
        tilesName: item.tileId.tilesName || item.tileId.displayName,
        dispatchedBoxes: dispatchedBoxes,
        receivedBoxes: alreadyReceivedBoxes,
        pendingBoxes: pendingBoxes,
        receivedFreshBoxes: 0,
        receivedFreshPieces: 0,
        receivedCornerDamagedBoxes: 0,
        receivedCornerDamagedPieces: 0,
        receivedFullDamagedBoxes: 0,
        receivedFullDamagedPieces: 0,
        tileDetails: item.tileId,
        tilesPiecesPerBox: item.tileId.tilesPiecesPerBox || 1,
        piecesPerBox: item.tileId.tilesPiecesPerBox || 1 // Keep for backward compatibility
      };
    });

    setForm(prev => ({
      ...prev,
      dispatchId: selected,
      items: items
    }));
  };

  const updateItemField = (index, field, value) => {
    const updatedItems = [...form.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: parseInt(value) || 0
    };

    setForm(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation 1: Check dispatch selection
    if (!form.dispatchId) {
      await Swal.fire({
        icon: 'warning',
        title: 'Dispatch Required',
        text: 'Please select a dispatch entry before creating receipt',
        confirmButtonColor: '#3B82F6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validation 2: Check location selection
    if (!form.location) {
      await Swal.fire({
        icon: 'warning',
        title: 'Location Required',
        text: 'Please select a godown/location where stock will be received',
        confirmButtonColor: '#3B82F6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validation 3: Check receipt date
    if (!form.receiptDate) {
      await Swal.fire({
        icon: 'warning',
        title: 'Receipt Date Required',
        text: 'Please select the receipt date',
        confirmButtonColor: '#3B82F6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validation 4: Check if at least one quantity is entered
    const hasValidItems = form.items.some(item => 
      item.receivedFreshBoxes > 0 || item.receivedFreshPieces > 0 || 
      item.receivedCornerDamagedBoxes > 0 || item.receivedCornerDamagedPieces > 0 ||
      item.receivedFullDamagedBoxes > 0 || item.receivedFullDamagedPieces > 0
    );

    if (!hasValidItems) {
      await Swal.fire({
        icon: 'warning',
        title: 'Quantities Required',
        text: 'Please enter at least one received quantity (boxes or pieces)',
        confirmButtonColor: '#3B82F6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validation 5: Check if received quantities don't exceed pending quantities
    // This includes both boxes received AND boxes opened for pieces
    const invalidItems = form.items.filter(item => {
      const totalReceivedBoxes = item.receivedFreshBoxes + item.receivedCornerDamagedBoxes + item.receivedFullDamagedBoxes;
      const totalPieces = item.receivedFreshPieces + item.receivedCornerDamagedPieces + item.receivedFullDamagedPieces;
      const boxesFromPieces = totalPieces > 0 && item.tilesPiecesPerBox ? 
                              Math.floor(totalPieces / item.tilesPiecesPerBox) : 0;
      const totalBoxesToReceive = totalReceivedBoxes + boxesFromPieces;
      
      // Check against pending boxes (not dispatched boxes)
      return totalBoxesToReceive > (item.pendingBoxes || item.dispatchedBoxes);
    });

    if (invalidItems.length > 0) {
      const itemDetails = invalidItems.map(item => {
        const totalReceivedBoxes = item.receivedFreshBoxes + item.receivedCornerDamagedBoxes + item.receivedFullDamagedBoxes;
        const totalPieces = item.receivedFreshPieces + item.receivedCornerDamagedPieces + item.receivedFullDamagedPieces;
        const boxesFromPieces = totalPieces > 0 && item.tilesPiecesPerBox ? 
                                Math.floor(totalPieces / item.tilesPiecesPerBox) : 0;
        const totalBoxesToReceive = totalReceivedBoxes + boxesFromPieces;
        const pending = item.pendingBoxes || item.dispatchedBoxes;
        return `<strong>${item.tilesName}</strong>: Trying to receive ${totalBoxesToReceive} boxes (${totalReceivedBoxes} full boxes + ${boxesFromPieces} opened for pieces) but only ${pending} boxes are pending`;
      }).join('<br><br>');
      
      await Swal.fire({
        icon: 'error',
        title: 'Exceeds Pending Quantity',
        html: `Total received quantity (boxes + opened boxes for pieces) cannot exceed pending quantity:<br><br>${itemDetails}`,
        confirmButtonColor: '#EF4444',
        confirmButtonText: 'OK, I\'ll Fix It'
      });
      return;
    }

    // Validation 6: Check if sum of pieces is divisible by tilesPiecesPerBox
    const invalidPiecesItems = form.items.filter(item => {
      const totalPieces = item.receivedFreshPieces + item.receivedCornerDamagedPieces + item.receivedFullDamagedPieces;
      if (totalPieces > 0 && item.tilesPiecesPerBox) {
        return totalPieces % item.tilesPiecesPerBox !== 0;
      }
      return false;
    });

    if (invalidPiecesItems.length > 0) {
      const itemDetails = invalidPiecesItems.map(item => {
        const totalPieces = item.receivedFreshPieces + item.receivedCornerDamagedPieces + item.receivedFullDamagedPieces;
        return `<strong>${item.tilesName}</strong>: Total pieces = ${totalPieces}, but pieces per box = ${item.tilesPiecesPerBox}. Must be divisible!`;
      }).join('<br><br>');
      
      await Swal.fire({
        icon: 'error',
        title: 'Invalid Pieces Count',
        html: `Sum of fresh and damaged pieces must be divisible by pieces per box (represents opened boxes):<br><br>${itemDetails}`,
        confirmButtonColor: '#EF4444',
        confirmButtonText: 'OK, I\'ll Fix It',
        footer: '<small>Example: If pieces per box = 3, then total pieces must be 3, 6, 9, etc.</small>'
      });
      return;
    }

    // Validation 7: Check delivery order commitment minimums
    if (Object.keys(commitments).length > 0) {
      const insufficientItems = form.items.filter(item => {
        const commitment = commitments[item.tileId];
        return commitment && item.receivedFreshBoxes < commitment.minRequiredFreshBoxes;
      });

      if (insufficientItems.length > 0) {
        const itemDetails = insufficientItems.map(item => {
          const commitment = commitments[item.tileId];
          return `<strong>${item.tilesName}</strong>: Entering ${item.receivedFreshBoxes} fresh boxes, but minimum <strong>${commitment.minRequiredFreshBoxes}</strong> required${commitment.doNumbers?.length > 0 ? ` (DO: ${commitment.doNumbers.join(', ')})` : ''}`;
        }).join('<br><br>');

        await Swal.fire({
          icon: 'error',
          title: 'Insufficient Fresh Boxes for Delivery Orders',
          html: `The following tiles have pending delivery orders that require minimum fresh boxes:<br><br>${itemDetails}<br><br><em>You must receive at least the minimum fresh boxes to cover delivery order commitments.</em>`,
          confirmButtonColor: '#EF4444',
          confirmButtonText: 'OK, I\'ll Fix It'
        });
        return;
      }
    }

    try {
      setSubmitting(true);

      const receiptData = {
        dispatchId: form.dispatchId.value,
        location: form.location.value,
        items: form.items
          .filter(item => 
            item.receivedFreshBoxes > 0 || item.receivedFreshPieces > 0 || 
            item.receivedCornerDamagedBoxes > 0 || item.receivedCornerDamagedPieces > 0 ||
            item.receivedFullDamagedBoxes > 0 || item.receivedFullDamagedPieces > 0
          )
          .map(item => ({
            tileId: item.tileId,
            orderedBoxes: item.dispatchedBoxes,
            receivedFreshBoxes: item.receivedFreshBoxes,
            receivedFreshPieces: item.receivedFreshPieces,
            receivedCornerDamagedBoxes: item.receivedCornerDamagedBoxes,
            receivedCornerDamagedPieces: item.receivedCornerDamagedPieces,
            receivedFullDamagedBoxes: item.receivedFullDamagedBoxes,
            receivedFullDamagedPieces: item.receivedFullDamagedPieces
          })),
        receiptDate: form.receiptDate,
        remarks: form.remarks,
        createdBy: user?.user?._id
      };

      const response = await api.post('/tiles-received', receiptData);

      if (response.data) {
        toast.success('Tiles received record created successfully!');
        resetForm();
        fetchReceipts();
        fetchInitialData(); // Refresh initial data to update dispatches
      }
    } catch (error) {
      console.error('Error creating tiles received record:', error);
      
      // Check if error response has a message from backend
      await Swal.fire({
        icon: 'error',
        title: 'Error Creating Receipt',
        text: error.response?.data?.message || error.message || 'Error creating tiles received record',
        confirmButtonColor: '#3B82F6'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(initializeFormState(user?.user?._id));
    setSelectedDispatch(null);
    setIsEditing(false);
    setReceiptId(null);
  };

  const handleViewReceipt = async (receipt) => {
    try {
      setLoading(true);
      const response = await api.get(`/tiles-received/${receipt._id}`);
      
      if (response.data && response.data.success) {
        setSelectedReceipt(response.data.data);
        setShowViewModal(true);
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch receipt details',
          confirmButtonColor: '#3B82F6'
        });
      }
    } catch (error) {
      console.error('Error fetching receipt details:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error Fetching Receipt',
        text: error.response?.data?.message || error.message || 'Error fetching receipt details',
        confirmButtonColor: '#3B82F6'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReceipt = async (receipt) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Receipt?',
      html: `<div style="text-align:left">
        <p>Are you sure you want to delete <b>${receipt.receiptId}</b>?</p>
        <p style="margin-top:8px;font-size:0.9em;color:#ef4444">This will reverse all stock changes and delete the related inward entry.</p>
      </div>`,
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const response = await api.delete(`/tiles-received/${receipt._id}`, {
        data: { deletedBy: user?.user?._id }
      });

      if (response.data?.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        fetchReceipts();
        fetchInitialData();
      }
    } catch (error) {
      console.error('Error deleting receipt:', error);
      const issues = error.response?.data?.issues;
      await Swal.fire({
        icon: 'error',
        title: 'Cannot Delete',
        html: issues
          ? `<div style="text-align:left"><p>${error.response.data.message}</p><ul style="margin-top:8px;font-size:0.85em;list-style:disc;padding-left:16px">${issues.map(i => `<li>${i}</li>`).join('')}</ul></div>`
          : (error.response?.data?.message || 'Failed to delete receipt'),
        confirmButtonColor: '#3B82F6'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaClock },
      'in-transit': { color: 'bg-blue-100 text-blue-800', icon: FaShoppingCart },
      partial: { color: 'bg-orange-100 text-orange-800', icon: FaBox },
      completed: { color: 'bg-green-100 text-green-800', icon: FaCheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: FaBan }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const dispatchOptions = filteredDispatches.map(dispatch => ({
    value: dispatch._id,
    label: `${dispatch.dispatchId} - Order: ${dispatch.orderId?.orderId} - ${dispatch.supplier?.name}`,
    data: dispatch
  }));

  const locationOptions = locations.map(location => ({
    value: location._id,
    label: location.name,
    data: location
  }));

  // Enhanced custom styles for react-select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '44px',
      fontSize: '14px',
      borderWidth: '2px',
      borderColor: state.isFocused ? '#3b82f6' : darkMode ? '#4b5563' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      borderRadius: '10px',
      backgroundColor: darkMode ? '#374151' : state.isFocused ? '#fefefe' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: '#3b82f6',
        backgroundColor: darkMode ? '#374151' : '#fefefe'
      }
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '14px',
      padding: '10px 12px',
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? darkMode ? '#374151' : '#eff6ff' 
          : darkMode ? '#1f2937' : 'white',
      color: state.isSelected ? 'white' : darkMode ? '#f3f4f6' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3b82f6' : darkMode ? '#374151' : '#eff6ff'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#9ca3af',
      fontWeight: '400'
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#1f2937',
      fontWeight: '500'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '10px',
      border: darkMode ? '2px solid #4b5563' : '2px solid #e5e7eb',
      backgroundColor: darkMode ? '#1f2937' : 'white',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    }),
    menuList: (base) => ({
      ...base,
      borderRadius: '8px'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#6b7280',
      '&:hover': {
        color: '#3b82f6'
      }
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#1f2937'
    })
  };

  // Loading state with enhanced UI
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50'}`}>
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent absolute top-0"></div>
          </div>
          <p className={`mt-4 text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading orders and locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="w-full mx-auto space-y-6">

      {/* Receipt Form */}
      <div ref={formRef} className={`rounded-2xl shadow-xl border-2 ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-8">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
              <FaClipboardCheck className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white mb-2">
                {isEditing ? 'Edit Tiles Received' : 'Record Tiles Received'}
              </h1>
              <p className="text-white/90">Record tiles received against dispatches and create inward entries</p>
            </div>
            {isEditing && (
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-all backdrop-blur-sm"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Receipt Information Section */}
          <div className={`rounded-xl border-2 p-6 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <FaBox className="text-indigo-600" />
              Receipt Information
            </h3>

            {/* Order and Location Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Select Dispatch <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowDispatchModal(true)}
                  className={`w-full p-3 border-2 rounded-xl text-left transition-all focus:outline-none focus:border-indigo-400 flex items-center justify-between ${darkMode ? 'bg-gray-600 border-gray-500 hover:border-indigo-400 disabled:bg-gray-700 disabled:cursor-not-allowed' : 'border-gray-300 hover:border-indigo-400 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed'}`}
                >
                  <span className={selectedDispatch ? (darkMode ? "text-white font-medium" : "text-gray-900 font-medium") : (darkMode ? "text-gray-400" : "text-gray-500")}>
                    {selectedDispatch ? selectedDispatch.label : "Click to select dispatch..."}
                  </span>
                  <FaChevronDown className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                </button>
                {loading && (
                  <div className="text-sm text-indigo-600 mt-1 flex items-center gap-2">
                    <FaSpinner className="animate-spin" />
                    Loading dispatches...
                  </div>
                )}
              </div>

              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Location (Godown) <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowLocationModal(true)}
                  className={`w-full p-3 border-2 rounded-xl text-left transition-all focus:outline-none focus:border-indigo-400 flex items-center justify-between ${darkMode ? 'bg-gray-600 border-gray-500 hover:border-indigo-400 disabled:bg-gray-700 disabled:cursor-not-allowed' : 'border-gray-300 hover:border-indigo-400 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed'}`}
                >
                  <span className={form.location ? (darkMode ? "text-white font-medium" : "text-gray-900 font-medium") : (darkMode ? "text-gray-400" : "text-gray-500")}>
                    {form.location ? form.location.label : "Click to select location..."}
                  </span>
                  <FaChevronDown className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                </button>
              </div>

              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Receipt Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.receiptDate}
                  onChange={(e) => setForm(prev => ({ ...prev, receiptDate: e.target.value }))}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-indigo-400 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white disabled:bg-gray-700' : 'border-gray-300 bg-white disabled:bg-gray-100'}`}
                  disabled={submitting}
                  required
                />
              </div>
            </div>
          </div>

          {/* Dispatch Details */}
          {selectedDispatch && (
            <div className={`rounded-xl border-2 p-6 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-indigo-50 border-indigo-200'}`}>
              <h3 className={`font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <FaBox className="w-4 h-4 text-white" />
                </div>
                Dispatch Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Dispatch ID:</span>
                  <div className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedDispatch.data.dispatchId}</div>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Supplier:</span>
                  <div className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedDispatch.data.supplier?.name}</div>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Status:</span>
                  <div>{getStatusBadge(selectedDispatch.data.status)}</div>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Dispatch Date:</span>
                  <div className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(selectedDispatch.data.dispatchDate)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Items Section */}
          {form.items.length > 0 && (
            <div className={`rounded-xl border-2 p-6 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
              <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FaBox className="text-indigo-600" />
                Received Items
              </h3>

              {/* Delivery Order Commitment Warning Banner */}
              {Object.keys(commitments).length > 0 && (
                <div className={`mb-4 p-4 rounded-xl border-2 ${darkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex items-start gap-3">
                    <FaExclamationTriangle className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className={`font-bold text-sm mb-2 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                        Delivery Order Commitments - Minimum Fresh Boxes Required
                      </h4>
                      <div className="space-y-1">
                        {Object.entries(commitments).map(([tileId, info]) => (
                          <div key={tileId} className={`text-sm ${darkMode ? 'text-red-200' : 'text-red-700'}`}>
                            <strong>{info.tileName}:</strong> Min <span className="font-bold text-red-600">{info.minRequiredFreshBoxes}</span> fresh boxes required
                            {info.doNumbers?.length > 0 && <span className="opacity-75"> (DO: {info.doNumbers.join(', ')})</span>}
                            {info.reason === 'negative_stock' && <span className="opacity-75"> - Stock deficit: {info.currentStock} boxes</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {commitmentsLoading && (
                <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                  <FaSpinner className="w-4 h-4 animate-spin text-indigo-500" />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Checking delivery order commitments...</span>
                </div>
              )}
              <div className={`overflow-x-auto rounded-xl border-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <table className="w-full">
                  <thead className={`bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800' : 'from-indigo-600 to-purple-600'} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">
                        Tile Details
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Dispatched<br/>Boxes
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Already<br/>Received
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Pending<br/>Boxes
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Fresh<br/>Boxes
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Fresh<br/>Pieces
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Corner Dmg<br/>Boxes
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Corner Dmg<br/>Pieces
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Full Dmg<br/>Boxes
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">
                        Full Dmg<br/>Pieces
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                    {form.items.map((item, index) => (
                      <tr key={index} className={`transition-colors ${commitments[item.tileId] ? (darkMode ? 'bg-red-900/20' : 'bg-red-50/50') : ''} ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-indigo-50'}`}>
                        <td className="px-4 py-4">
                          <div>
                            <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.tilesName}</div>
                            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ID: {item.tileDetails?.id} | {item.piecesPerBox} pcs/box
                            </div>
                            {item.tileDetails && (
                              <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {item.tileDetails.brand?.name} • {item.tileDetails.tilesCategory?.name}
                              </div>
                            )}
                            {commitments[item.tileId] && (
                              <div className="mt-1.5 px-2 py-1 rounded-md bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700">
                                <span className="text-xs font-bold text-red-600 dark:text-red-400">
                                  Min {commitments[item.tileId].minRequiredFreshBoxes} fresh boxes required
                                </span>
                                {commitments[item.tileId].doNumbers?.length > 0 && (
                                  <span className="text-xs text-red-500 dark:text-red-400 ml-1">
                                    (DO: {commitments[item.tileId].doNumbers.join(', ')})
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="font-bold text-indigo-600">
                            {formatNumberWithCommas(item.dispatchedBoxes)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="font-bold text-green-600">
                            {formatNumberWithCommas(item.receivedBoxes)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`font-bold ${item.pendingBoxes > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                            {formatNumberWithCommas(item.pendingBoxes)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="number"
                            min="0"
                            max={item.pendingBoxes}
                            value={item.receivedFreshBoxes}
                            onChange={(e) => updateItemField(index, 'receivedFreshBoxes', e.target.value)}
                            className={`w-20 px-3 py-2 border-2 rounded-lg text-center focus:outline-none transition-all ${
                              commitments[item.tileId] && item.receivedFreshBoxes < commitments[item.tileId].minRequiredFreshBoxes
                                ? 'border-red-500 bg-red-50 dark:bg-red-900/30 focus:border-red-400 ring-1 ring-red-300'
                                : darkMode ? 'bg-gray-600 border-gray-500 text-white disabled:bg-gray-700' : 'border-gray-300 disabled:bg-gray-50'
                            } ${darkMode && !(commitments[item.tileId] && item.receivedFreshBoxes < commitments[item.tileId].minRequiredFreshBoxes) ? '' : ''}`}
                            disabled={submitting || item.pendingBoxes <= 0}
                          />
                          {commitments[item.tileId] && item.receivedFreshBoxes < commitments[item.tileId].minRequiredFreshBoxes && (
                            <div className="text-xs text-red-600 font-semibold mt-1">
                              Min: {commitments[item.tileId].minRequiredFreshBoxes}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="number"
                            min="0"
                            value={item.receivedFreshPieces}
                            onChange={(e) => updateItemField(index, 'receivedFreshPieces', e.target.value)}
                            className={`w-20 px-3 py-2 border-2 rounded-lg text-center focus:outline-none focus:border-indigo-400 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white disabled:bg-gray-700' : 'border-gray-300 disabled:bg-gray-50'}`}
                            disabled={submitting}
                          />
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="number"
                            min="0"
                            max={item.pendingBoxes}
                            value={item.receivedCornerDamagedBoxes}
                            onChange={(e) => updateItemField(index, 'receivedCornerDamagedBoxes', e.target.value)}
                            className={`w-20 px-3 py-2 border-2 rounded-lg text-center focus:outline-none focus:border-yellow-400 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white disabled:bg-gray-700' : 'border-gray-300 disabled:bg-gray-50'}`}
                            disabled={submitting || item.pendingBoxes <= 0}
                          />
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="number"
                            min="0"
                            value={item.receivedCornerDamagedPieces}
                            onChange={(e) => updateItemField(index, 'receivedCornerDamagedPieces', e.target.value)}
                            className={`w-20 px-3 py-2 border-2 rounded-lg text-center focus:outline-none focus:border-yellow-400 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white disabled:bg-gray-700' : 'border-gray-300 disabled:bg-gray-50'}`}
                            disabled={submitting}
                          />
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="number"
                            min="0"
                            max={item.pendingBoxes}
                            value={item.receivedFullDamagedBoxes}
                            onChange={(e) => updateItemField(index, 'receivedFullDamagedBoxes', e.target.value)}
                            className={`w-20 px-3 py-2 border-2 rounded-lg text-center focus:outline-none focus:border-red-400 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white disabled:bg-gray-700' : 'border-gray-300 disabled:bg-gray-50'}`}
                            disabled={submitting || item.pendingBoxes <= 0}
                          />
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="number"
                            min="0"
                            value={item.receivedFullDamagedPieces}
                            onChange={(e) => updateItemField(index, 'receivedFullDamagedPieces', e.target.value)}
                            className={`w-20 px-3 py-2 border-2 rounded-lg text-center focus:outline-none focus:border-red-400 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white disabled:bg-gray-700' : 'border-gray-300 disabled:bg-gray-50'}`}
                            disabled={submitting}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                <div className="flex items-start gap-2">
                  <FaExclamationTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    <strong>Note:</strong> Fresh, corner damaged, and full damaged boxes will be converted to stock. 
                    Received quantities cannot exceed pending quantities. 
                    Sum of all pieces (fresh + corner damaged + full damaged) must be divisible by pieces per box 
                    (pieces represent opened boxes, e.g., 3 pieces from a 3-piece box = 1 box opened). 
                    In-transit quantities will be reduced by total boxes received plus opened boxes.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Remarks */}
          <div className={`rounded-xl border-2 p-6 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Remarks
            </label>
            <textarea
              value={form.remarks}
              onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
              rows={3}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-indigo-400 transition-all resize-none ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
              placeholder="Enter any additional remarks..."
              disabled={submitting}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={resetForm}
              className={`px-6 py-3 border-2 rounded-xl transition-all font-semibold disabled:opacity-50 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
              disabled={submitting}
            >
              Reset
            </button>
            <button
              type="submit"
              className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-bold"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <FaClipboardCheck className="w-5 h-5" />
                  {isEditing ? 'Update Receipt' : 'Create Receipt'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Receipts List */}
      <div className={`rounded-2xl shadow-xl border-2 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`p-6 border-b-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Receipts List
            </h2>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md"
            >
              <FaFilter className="w-4 h-4" />
              Filters
            </button>
          </div>

          {/* Search */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search receipts..."
                  value={receiptsSearchTerm}
                  onChange={(e) => setReceiptsSearchTerm(e.target.value)}
                  className={`w-full px-4 py-3 pl-12 border-2 rounded-xl focus:outline-none focus:border-indigo-400 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-indigo-50 border-indigo-200'}`}>
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Dispatch ID</label>
                <input
                  type="text"
                  value={filters.dispatchId}
                  onChange={(e) => setFilters(prev => ({ ...prev, dispatchId: e.target.value }))}
                  placeholder="Filter by dispatch ID..."
                  className={`w-full px-3 py-2 border-2 rounded-xl focus:outline-none focus:border-indigo-400 ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className={`w-full px-3 py-2 border-2 rounded-xl focus:outline-none focus:border-indigo-400 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className={`w-full px-3 py-2 border-2 rounded-xl focus:outline-none focus:border-indigo-400 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Actions</label>
                <button
                  onClick={() => {
                    setFilters({
                      orderId: '',
                      supplier: '',
                      location: '',
                      startDate: '',
                      endDate: ''
                    });
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                  className="w-full px-3 py-2 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-all"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Receipts Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <div className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading receipts...</div>
            </div>
          ) : filteredReceipts.length > 0 ? (
            <table className="w-full">
              <thead className={`bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800' : 'from-indigo-600 to-purple-600'} text-white`}>
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Receipt Details
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Dispatch Info
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Items Received
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Inward Entry
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                {filteredReceipts.map((receipt) => (
                  <tr key={receipt._id} className={`transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-indigo-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{receipt.receiptId}</div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(receipt.receiptDate)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{receipt.dispatchId?.dispatchId}</div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{receipt.supplier?.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <FaWarehouse className="w-4 h-4 text-gray-400" />
                        <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{receipt.location?.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{receipt.items?.length} items</div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Fresh: {receipt.items?.reduce((sum, item) => sum + (item.receivedFreshBoxes || 0), 0)} boxes
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {receipt.inwardEntryId ? (
                        <div>
                          <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{receipt.inwardEntryId.inwardId}</div>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-1 ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                            <FaCheckCircle className="w-3 h-3" />
                            Created
                          </span>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800'}`}>
                          <FaClock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewReceipt(receipt)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="View Receipt"
                        >
                          <FaEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteReceipt(receipt)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Delete Receipt"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No receipts found</div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className={`px-6 py-3 border-t flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Showing {((pagination.currentPage - 1) * 10) + 1} to {Math.min(pagination.currentPage * 10, pagination.totalItems)} of {pagination.totalItems} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={!pagination.hasPrev}
                className={`px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Previous
              </button>
              <span className="px-3 py-1 bg-blue-500 text-white rounded">
                {pagination.currentPage}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={!pagination.hasNext}
                className={`px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Dispatch Selection Modal */}
      {showDispatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b-2 flex justify-between items-center bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800 border-gray-700' : 'from-indigo-600 to-purple-600 border-gray-200'} rounded-t-2xl`}>
              <h3 className="text-xl font-bold text-white">Select Dispatch</h3>
              <button
                onClick={() => setShowDispatchModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className={`p-6 border-b-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search dispatches by ID, supplier, or status..."
                  value={dispatchSearch}
                  onChange={(e) => setDispatchSearch(e.target.value)}
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:border-indigo-400 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {filteredDispatchesModal.length === 0 ? (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <FaShoppingCart className={`w-8 h-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <p className="text-lg font-medium">No dispatches found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredDispatchesModal.map((dispatch) => (
                    <div
                      key={dispatch._id}
                      onClick={() => {
                        handleDispatchSelect({ 
                          value: dispatch._id, 
                          label: `${dispatch.dispatchId} - ${dispatch.supplier?.name} (${dispatch.status})`, 
                          data: dispatch 
                        });
                        setShowDispatchModal(false);
                        setDispatchSearch('');
                      }}
                      className={`border-2 rounded-xl p-4 transition-all duration-200 cursor-pointer ${darkMode ? 'border-gray-600 hover:border-indigo-400 hover:bg-gray-700' : 'border-gray-200 hover:border-indigo-500 hover:bg-indigo-50'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dispatch #{dispatch.dispatchId}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          dispatch.status === 'pending' ? (darkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800') :
                          dispatch.status === 'in-transit' ? (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800') :
                          dispatch.status === 'partial' ? (darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800') :
                          (darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800')
                        }`}>
                          {dispatch.status}
                        </span>
                      </div>
                      <p className={`text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Supplier: {dispatch.supplier?.name || 'N/A'}
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Items: {dispatch.items?.length || 0} | 
                        Expected: {dispatch.expectedDeliveryDate ? new Date(dispatch.expectedDeliveryDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Location Selection Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b-2 flex justify-between items-center bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800 border-gray-700' : 'from-indigo-600 to-purple-600 border-gray-200'} rounded-t-2xl`}>
              <h3 className="text-xl font-bold text-white">Select Location</h3>
              <button
                onClick={() => setShowLocationModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search locations by name or address..."
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {filteredLocationsModal.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FaWarehouse className={`mx-auto h-12 w-12 mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <p>No locations found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredLocationsModal.map((location) => (
                    <div
                      key={location._id}
                      onClick={() => {
                        setForm(prev => ({ 
                          ...prev, 
                          location: { 
                            value: location._id, 
                            label: location.name, 
                            data: location 
                          } 
                        }));
                        setShowLocationModal(false);
                        setLocationSearch('');
                      }}
                      className={`border rounded-lg p-4 transition-all duration-200 cursor-pointer ${darkMode ? 'border-gray-600 hover:border-blue-400 hover:shadow-md hover:bg-gray-700' : 'hover:border-blue-500 hover:shadow-md'}`}
                    >
                      <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{location.name}</h4>
                      {location.address && (
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{location.address}</p>
                      )}
                      {location.type && (
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type: {location.type}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Receipt Modal */}
      {showViewModal && selectedReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className={`bg-gradient-to-r ${darkMode ? 'from-blue-800 to-indigo-800' : 'from-blue-600 to-indigo-600'} px-6 py-4 flex justify-between items-center`}>
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <FaClipboardCheck className="w-6 h-6" />
                  Receipt Details
                </h3>
                <p className="text-blue-100 text-sm mt-1">{selectedReceipt.receiptId}</p>
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedReceipt(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Receipt Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gradient-to-br from-blue-900/50 to-indigo-900/50 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'}`}>
                  <div className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Dispatch ID</div>
                  <div className={`text-lg font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{selectedReceipt.dispatchId?.dispatchId || 'N/A'}</div>
                </div>
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'}`}>
                  <div className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Supplier</div>
                  <div className={`text-lg font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{selectedReceipt.supplier?.name || 'N/A'}</div>
                </div>
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gradient-to-br from-green-900/50 to-emerald-900/50 border-green-700' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'}`}>
                  <div className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Location</div>
                  <div className={`text-lg font-bold flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    <FaWarehouse className="w-4 h-4" />
                    {selectedReceipt.location?.name || 'N/A'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gradient-to-br from-yellow-900/50 to-amber-900/50 border-yellow-700' : 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200'}`}>
                  <div className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Receipt Date</div>
                  <div className={`text-lg font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    {new Date(selectedReceipt.receiptDate).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                </div>
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gradient-to-br from-teal-900/50 to-cyan-900/50 border-teal-700' : 'bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200'}`}>
                  <div className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Inward Entry</div>
                  <div className={`text-lg font-bold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                    {selectedReceipt.inwardEntryId ? (
                      <span className="inline-flex items-center gap-2">
                        <FaCheckCircle className="w-4 h-4 text-green-500" />
                        {typeof selectedReceipt.inwardEntryId === 'object' 
                          ? selectedReceipt.inwardEntryId.inwardId 
                          : selectedReceipt.inwardEntryId}
                      </span>
                    ) : (
                      <span className="text-yellow-600 inline-flex items-center gap-2">
                        <FaClock className="w-4 h-4" />
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Items Received */}
              <div className={`rounded-xl border-2 overflow-hidden ${darkMode ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200'}`}>
                <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600' : 'bg-gradient-to-r from-gray-100 to-slate-100 border-gray-300'}`}>
                  <h4 className={`text-lg font-bold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <FaBox className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                    Items Received
                  </h4>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {selectedReceipt.items?.map((item, index) => (
                      <div key={index} className={`p-5 rounded-xl border-2 transition-all shadow-sm hover:shadow-md ${darkMode ? 'bg-gray-700 border-gray-600 hover:border-blue-500' : 'bg-white border-gray-200 hover:border-blue-300'}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.tileId?.tilesName || item.tileId || 'Unknown Tile'}
                            </div>
                            {item.tileId && typeof item.tileId === 'object' && (
                              <div className={`flex flex-wrap gap-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                <span className="flex items-center gap-1">
                                  <span className="font-semibold">ID:</span>
                                  <span className="text-blue-600">{item.tileId.id}</span>
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="font-semibold">Brand:</span>
                                  <span>{item.tileId.brand?.name || 'N/A'}</span>
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="font-semibold">Pieces/Box:</span>
                                  <span className="font-bold text-purple-600">{item.tileId.tilesPiecesPerBox || 'N/A'}</span>
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-xl font-bold text-sm">
                            Item #{index + 1}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gradient-to-br from-green-900/50 to-emerald-900/50 border-green-700' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'}`}>
                            <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh Boxes</div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{item.receivedFreshBoxes || 0}</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gradient-to-br from-blue-900/50 to-cyan-900/50 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200'}`}>
                            <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh Pieces</div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{item.receivedFreshPieces || 0}</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gradient-to-br from-red-900/50 to-pink-900/50 border-red-700' : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200'}`}>
                            <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Corner Dmg Boxes</div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{item.receivedCornerDamagedBoxes || 0}</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gradient-to-br from-orange-900/50 to-amber-900/50 border-orange-700' : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200'}`}>
                            <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Corner Dmg Pieces</div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{item.receivedCornerDamagedPieces || 0}</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gradient-to-br from-red-900/50 to-pink-900/50 border-red-700' : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200'}`}>
                            <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Full Dmg Boxes</div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{item.receivedFullDamagedBoxes || 0}</div>
                          </div>
                          <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gradient-to-br from-orange-900/50 to-amber-900/50 border-orange-700' : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200'}`}>
                            <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Full Dmg Pieces</div>
                            <div className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{item.receivedFullDamagedPieces || 0}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Remarks */}
              {selectedReceipt.remarks && (
                <div className={`p-5 rounded-xl border-2 ${darkMode ? 'bg-gradient-to-br from-amber-900/30 to-yellow-900/30 border-amber-700' : 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'}`}>
                  <div className={`text-sm font-bold mb-2 flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="text-lg">📝</span>
                    Remarks
                  </div>
                  <div className={darkMode ? 'text-white' : 'text-gray-900'}>{selectedReceipt.remarks}</div>
                </div>
              )}

              {/* Created By */}
              <div className={`flex justify-between items-center text-sm pt-4 border-t ${darkMode ? 'text-gray-400 border-gray-600' : 'text-gray-600 border-gray-200'}`}>
                <div>
                  <span className="font-semibold">Created by:</span> {selectedReceipt.createdBy?.name || 'Unknown'}
                </div>
                <div>
                  <span className="font-semibold">Created at:</span>{' '}
                  {new Date(selectedReceipt.createdAt).toLocaleString('en-IN')}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t flex justify-end ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedReceipt(null);
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-gray-600 to-slate-600 text-white rounded-xl hover:from-gray-700 hover:to-slate-700 shadow-md hover:shadow-lg transition-all font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default TilesReceivedEnhanced;